import { Typography, type TypographyProps } from '@mui/material';
import { styled } from '@mui/material/styles';

export type RecurringTimelineStatus = 'charged' | 'scheduled' | 'failed' | 'pending';

export interface StatusBadgeProps extends Omit<TypographyProps, 'children'> {
  status: RecurringTimelineStatus;
  children?: React.ReactNode;
}

const statusColorMap: Record<RecurringTimelineStatus, 'success.main' | 'info.main' | 'error.main' | 'warning.main'> = {
  charged: 'success.main',
  scheduled: 'info.main',
  failed: 'error.main',
  pending: 'warning.main',
};

const Badge = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'status',
})<{ status: RecurringTimelineStatus }>(({ status, theme }) => ({
  display: 'inline-block',
  padding: theme.spacing(0.25, 1),
  borderRadius: theme.shape.surface,
  color: theme.vars?.palette.background.paper ?? theme.palette.background.paper,
  backgroundColor: statusColorMap[status]
    .split('.')
    .reduce<unknown>((palette, key) => {
      if (typeof palette === 'object' && palette !== null && key in palette) {
        return (palette as Record<string, unknown>)[key];
      }

      return theme.vars?.palette.primary.main ?? theme.palette.primary.main;
    }, theme.vars?.palette ?? theme.palette) as string,
  textTransform: 'uppercase',
}));

export function StatusBadge({ status, children, sx, ...props }: StatusBadgeProps) {
  return (
    <Badge
      variant="caption"
      status={status}
      sx={sx}
      {...props}
    >
      {children ?? status}
    </Badge>
  );
}
